import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils/utils";
import { browserBaseline, faceOf, fontCss, textWidth } from "./fonts";
import { HighlightedSlotContext } from "./highlightedSlot";
import { LineContext } from "./line";
import {
  BASELINE_RATIO,
  MeasureText,
  NUMBER_GAP,
  baselineIn,
  bulletItemGap,
  cardBand,
  defaultLeading,
  layoutCard,
  leadingOf,
  signatureHeight,
  signatureLineTop,
  signatureTitleBand,
  textLeading,
  valueListStyle,
  wrapText
} from "./layout";
import {
  Line,
  Piece,
  PieceEntry,
  RichBlock,
  Run,
  endsWithStop,
  expandValues,
  isEmptyLines,
  parseRichText,
  visibleText
} from "./richText";
import {
  BulletsBlock,
  CardBlock,
  DocumentField,
  DocumentTheme,
  FixedItem,
  HeadingBlock,
  Inline,
  ParagraphBlock,
  ScriptBlock,
  SignatureBlock,
  TableBlock,
  TextStyle,
  componentTheme,
  isField,
  isLink,
  linkColorOf,
  textStyle
} from "./types";

// Everything here draws at 1px per PDF point; the page scales the result.

export type OnField = (slot: string) => void;

const measureText: MeasureText = (style, text) =>
  textWidth(style.font, style.size, text);

const textCss = (
  style: TextStyle,
  lineHeight?: number
): React.CSSProperties => ({
  ...fontCss(style.font),
  fontSize: style.size,
  color: style.color,
  lineHeight: `${lineHeight ?? leadingOf(style)}px`,
  // A word that couldn't be measured still breaks rather than overflowing its line.
  overflowWrap: "anywhere"
});

// Breaks words as the PDF does: a hyphenated word that fits stays whole, and a wider word breaks between any two characters.
const PdfWords: React.FC<{ text: string }> = ({ text }) => {
  const { width, style } = useContext(LineContext);
  const font = style?.font;
  const size = style?.size;
  // Measured once per text and line, not on every render of the page.
  const pieces = useMemo(
    () =>
      text.split(/(\S+)/).map((piece, index) => {
        if (index % 2 === 0) return { piece, kind: "space" as const };
        const wide =
          font !== undefined &&
          size !== undefined &&
          textWidth(font, size, piece) > width;
        if (wide) return { piece, kind: "wide" as const };
        return { piece, kind: /\S-\S/.test(piece) ? "whole" : "word" } as const;
      }),
    [text, width, font, size]
  );
  return (
    <>
      {pieces.map(({ piece, kind }, index) => {
        if (kind === "whole")
          return (
            <span key={index} className="whitespace-nowrap">
              {piece}
            </span>
          );
        if (kind === "wide")
          return (
            <span key={index}>
              {Array.from(piece).map((character, characterIndex) => (
                <React.Fragment key={characterIndex}>
                  {characterIndex > 0 ? <wbr /> : null}
                  {character}
                </React.Fragment>
              ))}
            </span>
          );
        return piece;
      })}
    </>
  );
};

const withBreaks = (value: string) =>
  value.split("\n").map((line, index) => (
    <React.Fragment key={index}>
      {index > 0 ? <br /> : null}
      <PdfWords text={line} />
    </React.Fragment>
  ));

// A run in its own face, its words broken against the line in that face.
const RichRun: React.FC<{ run: Run }> = ({ run }) => {
  const line = useContext(LineContext);
  const font = line.style && faceOf(line.style.font, run.bold, run.italic);
  if (!line.style || !font || font === line.style.font)
    return <PdfWords text={run.text} />;
  return (
    <span style={fontCss(font)}>
      <LineContext.Provider value={{ ...line, style: { ...line.style, font } }}>
        <PdfWords text={run.text} />
      </LineContext.Provider>
    </span>
  );
};

// A value's block as lines; with `marker`, a list item's marker starts its first line.
const RichLines: React.FC<{ lines: Line[]; marker?: string }> = ({
  lines,
  marker
}) => (
  <>
    {lines.map((line, index) => (
      <React.Fragment key={index}>
        {index > 0 ? <br /> : null}
        {index === 0 && marker ? <PdfWords text={`${marker} `} /> : null}
        {line.map((run, runIndex) => (
          <RichRun key={runIndex} run={run} />
        ))}
      </React.Fragment>
    ))}
  </>
);

// A multiline value kept inside one paragraph: its blocks one per line, each item led by its marker.
const RichValue: React.FC<{ value: string }> = ({ value }) => (
  <>
    {parseRichText(value).map((block, index) => (
      <React.Fragment key={index}>
        {index > 0 ? <br /> : null}
        <RichLines lines={block.lines} marker={block.marker} />
      </React.Fragment>
    ))}
  </>
);

// Where a field sits on the page: picked, located and tinted as one. A later block of a value is a
// `continuation`: clicked and tinted with it, but neither another tab stop nor another anchor.
const FieldShell: React.FC<{
  field: DocumentField;
  onField?: OnField;
  continuation?: boolean;
  children: (blank: boolean, marker?: string) => React.ReactNode;
}> = ({ field, onField, continuation = false, children }) => {
  const reachable = onField !== undefined && !continuation;
  const { t } = useTranslation();
  const blank = field.value === null || field.value === "";
  const highlighted = useContext(HighlightedSlotContext) === field.slot;
  // A CRM field (one with a source) reads "Missing CRM data" however it became empty, as the PDF does.
  const fromCrm = field.missing === true || field.source !== undefined;
  const marker = blank
    ? t(
        fromCrm
          ? "contracts.proposals.editor.missingCrmData"
          : "contracts.proposals.editor.needsInput"
      )
    : undefined;
  return (
    <span
      data-slot={field.slot}
      // Where the editor scrolls to locate a field; only on fields the rep can pick.
      data-slot-anchor={reachable ? field.slot : undefined}
      data-blank={blank ? (fromCrm ? "missing" : "needs-input") : undefined}
      aria-label={blank ? `${field.label ?? field.slot}: ${marker}` : undefined}
      title={field.label}
      // Picked from the keyboard too, so the field it opens is reachable without a pointer.
      role={reachable ? "button" : undefined}
      tabIndex={reachable ? 0 : undefined}
      // Keeps focus in the slot's input, so its tint doesn't blink while picked.
      onMouseDown={onField ? (event) => event.preventDefault() : undefined}
      onClick={onField ? () => onField(field.slot) : undefined}
      onKeyDown={
        onField
          ? (event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              onField(field.slot);
            }
          : undefined
      }
      // The draft PDF's marker, in the PDF's own colour; zero line height keeps its line from growing.
      // The hover and focus tints are the app's, since the PDF has none; a background moves no text.
      className={cn(
        blank && "text-[#B8540A] text-[length:min(1em,10px)] leading-[0]",
        onField && "cursor-pointer",
        onField &&
          blank &&
          !highlighted &&
          "rounded-sm hover:bg-badges-warning",
        highlighted && "rounded-sm bg-badges-natural"
      )}
      style={blank ? fontCss("Helvetica-Oblique") : undefined}
    >
      {children(blank, marker)}
    </span>
  );
};

export const FieldValue: React.FC<{
  field: DocumentField;
  onField?: OnField;
}> = ({ field, onField }) => (
  <FieldShell field={field} onField={onField}>
    {(blank, marker) => {
      if (blank) return marker;
      return field.multiline ? (
        <RichValue value={field.value as string} />
      ) : (
        withBreaks(field.value as string)
      );
    }}
  </FieldShell>
);

// A value ending a sentence with its own full stop ("Acme Inc.") takes the
// sentence's: the text right after it drops its leading one, as the PDF does.
const withSentenceStops = (parts: Inline[]): Inline[] =>
  parts.map((part, index) => {
    const before = parts[index - 1];
    return typeof part === "string" &&
      part.startsWith(".") &&
      before !== undefined &&
      endsWithStop(before)
      ? part.slice(1)
      : part;
  });

export const InlineText: React.FC<{
  parts: Inline[];
  linkColor?: string;
  onField?: OnField;
}> = ({ parts, linkColor, onField }) => (
  <>
    {withSentenceStops(parts).map((part, index) => {
      if (typeof part === "string") return <PdfWords key={index} text={part} />;
      if (isLink(part)) {
        return (
          <span key={index} style={{ color: linkColor }}>
            <PdfWords text={part.text} />
          </span>
        );
      }
      if (isField(part))
        return <FieldValue key={index} field={part} onField={onField} />;
      return null;
    })}
  </>
);

// One line already broken as the PDF breaks it, its baseline `baseline` points below its box's top.
const AtBaseline: React.FC<{
  x: number;
  baseline: number;
  style: TextStyle;
  children: React.ReactNode;
}> = ({ x, baseline, style, children }) => (
  <div
    className="absolute whitespace-pre"
    style={{
      left: x,
      top: baseline - BASELINE_RATIO * style.size,
      ...textCss(style, style.size)
    }}
  >
    {children}
  </div>
);

interface PdfTextProps {
  parts: Inline[];
  style: TextStyle;
  lineHeight: number;
  linkColor?: string;
  onField?: OnField;
}

// Moves text to the PDF's baseline, one font size below the line's top, without changing the block's height.
const PdfLine: React.FC<{
  style: TextStyle;
  lineHeight: number;
  children: React.ReactNode;
}> = ({ style, lineHeight, children }) => {
  const { width } = useContext(LineContext);
  const browser =
    browserBaseline(style.font, style.size, lineHeight) ??
    baselineIn(lineHeight, style.size);
  return (
    <span className="relative" style={{ top: style.size - browser }}>
      <LineContext.Provider value={{ width, style }}>
        {children}
      </LineContext.Provider>
    </span>
  );
};

const PdfText: React.FC<PdfTextProps> = ({
  parts,
  style,
  lineHeight,
  linkColor,
  onField
}) => (
  <PdfLine style={style} lineHeight={lineHeight}>
    <InlineText parts={parts} linkColor={linkColor} onField={onField} />
  </PdfLine>
);

// Text set in from its block's left edge has that much less of the line.
const Indented: React.FC<{ indent: number; children: React.ReactNode }> = ({
  indent,
  children
}) => {
  const line = useContext(LineContext);
  return (
    <LineContext.Provider value={{ ...line, width: line.width - indent }}>
      {children}
    </LineContext.Provider>
  );
};

// A block of text set in one style.
const TextBlock: React.FC<PdfTextProps> = (props) => (
  <div style={textCss(props.style, props.lineHeight)}>
    <PdfText {...props} />
  </div>
);

// A style with a fill sets its block on a band of that colour across the frame, the text fillPadding inside it.
export const OnFill: React.FC<{
  style: TextStyle;
  children: React.ReactNode;
}> = ({ style, children }) => {
  if (!style.fill) return <>{children}</>;
  const padding = style.fillPadding ?? 0;
  return (
    <div data-fill style={{ background: style.fill, padding }}>
      <Indented indent={2 * padding}>{children}</Indented>
    </div>
  );
};

const PageHeading: React.FC<{
  block: HeadingBlock;
  theme: DocumentTheme;
  onField?: OnField;
}> = ({ block, theme, onField }) => {
  const style = textStyle(theme, "h1");
  // Each line is a bar height tall, else the style's leading.
  const lineHeight = style.barHeight ?? leadingOf(style);
  return (
    <div>
      <div
        className="relative"
        style={{
          paddingLeft: style.indent ?? 0,
          ...textCss(style, lineHeight)
        }}
      >
        {style.bar ? (
          <div
            className="absolute inset-y-0 left-0"
            style={{ width: style.barWidth, background: style.bar }}
          />
        ) : null}
        <Indented indent={style.indent ?? 0}>
          <PdfText
            parts={block.text}
            style={style}
            lineHeight={lineHeight}
            linkColor={linkColorOf(theme)}
            onField={onField}
          />
        </Indented>
      </div>
      {/* The title keeps its rule gap with no rule; padding, since an empty block's margin collapses away. */}
      <div style={{ paddingTop: style.ruleGap ?? 0 }}>
        {style.rule ? (
          // A zero-height rule like the PDF's line.
          <div style={{ height: 0, boxShadow: `0 0 0 0.5px ${style.rule}` }} />
        ) : null}
      </div>
    </div>
  );
};

// Only a section's title draws the h1 bar; a heading block in the h1 style is plain text in that style.
export const Heading: React.FC<{
  block: HeadingBlock;
  theme: DocumentTheme;
  sectionTitle?: boolean;
  onField?: OnField;
}> = ({ block, theme, sectionTitle, onField }) => {
  if (sectionTitle)
    return <PageHeading block={block} theme={theme} onField={onField} />;
  const style = textStyle(theme, block.style);
  return (
    <OnFill style={style}>
      <TextBlock
        parts={block.text}
        style={style}
        lineHeight={textLeading(block, theme)}
        linkColor={linkColorOf(theme)}
        onField={onField}
      />
    </OnFill>
  );
};

// A value's list item: its text indented, and on its first block the dot or number beside the first line.
// An item with no text is no lines high, as in the PDF, its marker over the next line.
const ValueListItem: React.FC<{
  block: RichBlock;
  style: TextStyle;
  leading: number;
  theme: DocumentTheme;
  children: React.ReactNode;
}> = ({ block, style, leading, theme, children }) => {
  const { indent, dot } = valueListStyle(theme, style);
  return (
    <div data-value-item className="relative" style={{ paddingLeft: indent }}>
      {block.marker && block.listKind === "bullet" ? (
        <div
          data-value-dot
          className="absolute rounded-full"
          style={{
            left: dot.x - dot.radius,
            top: style.size - dot.rise - dot.radius,
            width: dot.radius * 2,
            height: dot.radius * 2,
            background: dot.color
          }}
        />
      ) : null}
      {block.marker && block.listKind === "number" ? (
        // In the text's font, ending a little before the item's text.
        <div
          data-value-number
          className="absolute left-0 top-0 whitespace-pre text-right"
          style={{ width: indent - NUMBER_GAP * style.size }}
        >
          <PdfLine style={style} lineHeight={leading}>
            {block.marker}
          </PdfLine>
        </div>
      ) : null}
      <Indented indent={indent}>
        <PdfLine style={style} lineHeight={leading}>
          {children}
        </PdfLine>
      </Indented>
    </div>
  );
};

// A paragraph whose value holds a list or a blank line: each piece its own lines, with no gap between.
const ExpandedParagraph: React.FC<{
  pieces: Piece[];
  style: TextStyle;
  leading: number;
  theme: DocumentTheme;
  onField?: OnField;
}> = ({ pieces, style, leading, theme, onField }) => {
  // Each value's first block is where it is reached from the keyboard and located.
  const firsts = new Set<PieceEntry>();
  const seen = new Set<DocumentField>();
  pieces.forEach((piece) =>
    piece.entries.forEach((entry) => {
      if ("part" in entry || seen.has(entry.field)) return;
      seen.add(entry.field);
      firsts.add(entry);
    })
  );
  return (
    <div style={textCss(style, leading)}>
      {pieces.map((piece, index) => {
        const { block } = piece;
        if (block && !block.listKind && isEmptyLines(block)) {
          return (
            <div key={index} data-blank-line style={{ height: leading }} />
          );
        }
        const content = piece.entries.map((entry, entryIndex) =>
          "part" in entry ? (
            <InlineText
              key={entryIndex}
              parts={[entry.part]}
              linkColor={linkColorOf(theme)}
              onField={onField}
            />
          ) : (
            <FieldShell
              key={entryIndex}
              field={entry.field}
              onField={onField}
              continuation={!firsts.has(entry)}
            >
              {() => <RichLines lines={entry.block.lines} />}
            </FieldShell>
          )
        );
        return block?.listKind ? (
          <ValueListItem
            key={index}
            block={block}
            style={style}
            leading={leading}
            theme={theme}
          >
            {content}
          </ValueListItem>
        ) : (
          <div key={index}>
            <PdfLine style={style} lineHeight={leading}>
              {content}
            </PdfLine>
          </div>
        );
      })}
    </div>
  );
};

// A paragraph's text without its band, so a paragraph split across pages can put each part on its own.
export const ParagraphText: React.FC<{
  block: ParagraphBlock;
  theme: DocumentTheme;
  onField?: OnField;
}> = ({ block, theme, onField }) => {
  const style = textStyle(theme, block.style);
  const leading = textLeading(block, theme);
  // A filled paragraph keeps a value inside its band, as one paragraph.
  const pieces = useMemo(
    () => (style.fill ? undefined : expandValues(block.text)),
    [style.fill, block.text]
  );
  return pieces ? (
    <ExpandedParagraph
      pieces={pieces}
      style={style}
      leading={leading}
      theme={theme}
      onField={onField}
    />
  ) : (
    <TextBlock
      parts={block.text}
      style={style}
      lineHeight={leading}
      linkColor={linkColorOf(theme)}
      onField={onField}
    />
  );
};

export const Paragraph: React.FC<{
  block: ParagraphBlock;
  theme: DocumentTheme;
  onField?: OnField;
}> = ({ block, theme, onField }) => (
  <OnFill style={textStyle(theme, block.style)}>
    <ParagraphText block={block} theme={theme} onField={onField} />
  </OnFill>
);

export const Script: React.FC<{ block: ScriptBlock; theme: DocumentTheme }> = ({
  block,
  theme
}) => {
  const style = textStyle(theme, block.style);
  return (
    <OnFill style={style}>
      <TextBlock
        parts={block.text}
        style={style}
        lineHeight={textLeading(block, theme)}
      />
    </OnFill>
  );
};

const BulletItem: React.FC<{
  parts: Inline[];
  style: TextStyle;
  leading: number;
  theme: DocumentTheme;
  first: boolean;
  onField?: OnField;
}> = ({ parts, style, leading, theme, first, onField }) => {
  const { indent, dot } = componentTheme(theme, "bullets");
  const baseline = style.size;
  return (
    <div
      data-item
      className="relative"
      style={{
        paddingLeft: indent,
        marginTop: first ? 0 : bulletItemGap(theme),
        ...textCss(style, leading)
      }}
    >
      <div
        className="absolute rounded-full"
        style={{
          left: dot.x - dot.radius,
          top: baseline - dot.rise - dot.radius,
          width: dot.radius * 2,
          height: dot.radius * 2,
          background: dot.color
        }}
      />
      <Indented indent={indent}>
        <PdfText
          parts={parts}
          style={style}
          lineHeight={leading}
          linkColor={linkColorOf(theme)}
          onField={onField}
        />
      </Indented>
    </div>
  );
};

export const Bullets: React.FC<{
  block: BulletsBlock;
  theme: DocumentTheme;
  from?: number;
  to?: number;
  onField?: OnField;
}> = ({ block, theme, from = 0, to = block.items.length, onField }) => (
  <div>
    {block.items.slice(from, to).map((item, index) => (
      <BulletItem
        key={from + index}
        parts={item}
        style={textStyle(theme, block.style)}
        leading={textLeading(block, theme)}
        theme={theme}
        first={index === 0}
        onField={onField}
      />
    ))}
  </div>
);

const TableRow: React.FC<{
  cells: React.ReactNode[];
  block: TableBlock;
  theme: DocumentTheme;
  variant: "head" | "row" | "total";
  rowIndex?: number;
}> = ({ cells, block, theme, variant, rowIndex }) => {
  const style = componentTheme(theme, "table");
  const lineHeight = defaultLeading(style.size);
  const { width: frameWidth } = useContext(LineContext);
  // The total row takes its own font and colour, else the header's.
  const cellStyle: TextStyle = {
    head: { font: style.headFont, size: style.size, color: style.headColor },
    row: { font: style.font, size: style.size, color: style.color },
    total: {
      font: style.totalFont ?? style.headFont,
      size: style.size,
      color: style.totalColor ?? style.headColor
    }
  }[variant];
  // Columns without a width share what the others leave of the frame, as in the PDF.
  const fixedWidth = block.columns.reduce(
    (sum, column) => sum + (column.width ?? 0),
    0
  );
  const flexibleColumns = block.columns.filter(
    (column) => !column.width
  ).length;
  return (
    <div
      data-head={variant === "head" ? "" : undefined}
      data-row={variant === "row" ? String(rowIndex) : undefined}
      data-total={variant === "total" ? "" : undefined}
      className="box-border flex items-center"
      style={{
        minHeight: style.rowHeight,
        padding: `${(style.rowHeight - lineHeight) / 2}px 0`,
        background:
          variant === "head"
            ? style.headFill
            : variant === "total"
              ? style.totalFill
              : undefined,
        // Drawn inside the row, so a row stays exactly rowHeight tall.
        boxShadow:
          variant === "head" || !style.rule
            ? undefined
            : `inset 0 -1px 0 ${style.rule}`,
        color: cellStyle.color,
        fontSize: style.size,
        lineHeight: `${lineHeight}px`,
        // A word that couldn't be measured still breaks rather than overflowing its cell.
        overflowWrap: "anywhere",
        ...fontCss(cellStyle.font)
      }}
    >
      {block.columns.map((column, index) => {
        const cell = cells[index];
        return (
          // Centred in its row like the PDF's cells, so a row with a wrapped cell centres the others.
          <div
            key={column.source}
            data-cell
            className="min-w-0"
            style={{
              flex: column.width ? `0 0 ${column.width}px` : "1 1 0",
              padding: `0 ${style.padding}px`,
              textAlign: column.align ?? "left"
            }}
          >
            <LineContext.Provider
              value={{
                width:
                  (column.width ||
                    (frameWidth - fixedWidth) / flexibleColumns) -
                  2 * style.padding,
                style: cellStyle
              }}
            >
              {typeof cell === "string" ? <PdfWords text={cell} /> : cell}
            </LineContext.Provider>
          </div>
        );
      })}
    </div>
  );
};

export const Table: React.FC<{
  block: TableBlock;
  theme: DocumentTheme;
  from?: number;
  to?: number;
  total?: boolean;
  onField?: OnField;
}> = ({
  block,
  theme,
  from = 0,
  to = block.rows.length,
  total = true,
  onField
}) => {
  const last = block.columns.length - 1;
  return (
    <div>
      <TableRow
        block={block}
        theme={theme}
        variant="head"
        cells={block.columns.map((column) => column.label)}
      />
      {block.rows.slice(from, to).map((row, index) => (
        <TableRow
          key={from + index}
          block={block}
          theme={theme}
          variant="row"
          rowIndex={from + index}
          cells={block.columns.map((column) => {
            const value = row[column.source];
            // A blank value takes no suffix: an empty cell, not a bare "per User/Month".
            return value ? value + (column.suffix ?? "") : value;
          })}
        />
      ))}
      {total && block.total ? (
        <TableRow
          block={block}
          theme={theme}
          variant="total"
          cells={block.columns.map((_, index) =>
            index === 0 ? (
              block.total?.label
            ) : index === last && block.total ? (
              <FieldValue field={block.total} onField={onField} />
            ) : null
          )}
        />
      ) : null}
    </div>
  );
};

export const Card: React.FC<{
  block: CardBlock;
  theme: DocumentTheme;
  width: number;
  onField?: OnField;
}> = ({ block, theme, width, onField }) => {
  const card = componentTheme(theme, "card");
  const band = cardBand(block, theme);
  const { texts, values, dividers, height } = layoutCard(
    block,
    theme,
    measureText
  );
  return (
    <div
      className="relative"
      style={{
        width,
        height,
        background: card.fill,
        // The PDF's rule width when the theme sets none.
        boxShadow: card.border
          ? `inset 0 0 0 ${card.borderWidth ?? 0.5}px ${card.border}`
          : undefined
      }}
    >
      {band ? (
        <div
          className="absolute inset-x-0 top-0"
          style={{ height: band.height, background: band.fill }}
        />
      ) : null}
      {dividers.map((y) => (
        <div
          key={y}
          className="absolute"
          style={{
            left: card.padding,
            right: card.padding,
            top: y,
            borderTop: `${card.divider.width}px solid ${card.divider.color}`
          }}
        />
      ))}
      {texts.map((text) => (
        <AtBaseline
          key={text.key}
          x={text.x}
          baseline={text.baseline}
          style={text.style}
        >
          {text.text}
        </AtBaseline>
      ))}
      {values.map(({ key, x, baseline, style, field, lines }) =>
        lines.map((line, index) => (
          <AtBaseline
            key={`${key}-${index}`}
            x={x}
            baseline={baseline + index * card.value.leading}
            style={style}
          >
            {/* An empty line inside a filled value is space, not a blank; a line is already plain text */}
            {line === "" ? null : (
              <FieldValue
                field={{ ...field, value: line, multiline: false }}
                onField={onField}
              />
            )}
          </AtBaseline>
        ))
      )}
    </div>
  );
};

// A row of signers side by side, columnPitch apart: each an optional title, then a line and a label per field.
export const Signature: React.FC<{
  row: SignatureBlock[];
  theme: DocumentTheme;
}> = ({ row, theme }) => {
  const signature = componentTheme(theme, "signature");
  const labelWidth = signature.labelWidth ?? 0;
  const title = signature.title;
  const padding = title?.fill ? (title.fillPadding ?? 0) : 0;
  return (
    <div className="relative" style={{ height: signatureHeight(row, theme) }}>
      {row.map((block, column) => {
        const left = column * (signature.columnPitch ?? 0);
        return (
          <React.Fragment key={column}>
            {title && block.title?.length ? (
              <>
                {title.fill ? (
                  // The band runs under the signer's label and line.
                  <div
                    data-signer-title
                    className="absolute top-0"
                    style={{
                      left,
                      width: labelWidth + signature.lineLength,
                      height: signatureTitleBand(title),
                      background: title.fill
                    }}
                  />
                ) : null}
                <AtBaseline
                  x={left + padding}
                  baseline={padding + title.size}
                  style={title}
                >
                  {block.title.join("")}
                </AtBaseline>
              </>
            ) : null}
            {block.fields.map((field, index) => {
              const line = signatureLineTop(index, row, theme);
              return (
                <React.Fragment key={field.type}>
                  <div
                    className="absolute"
                    style={{
                      left: left + labelWidth,
                      top: line,
                      width: signature.lineLength,
                      borderTop: `${signature.lineWidth}px solid ${signature.line}`
                    }}
                  />
                  <AtBaseline
                    x={left}
                    baseline={line + signature.labelGap}
                    style={textStyle(theme, "fieldLabel")}
                  >
                    {field.label}
                  </AtBaseline>
                </React.Fragment>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// Fields at fixed baselines. One allowed several lines wraps downwards, ends in "…" past its
// last line, and moves every field below it down by the lines it added.
export const FixedItems: React.FC<{
  items: FixedItem[];
  pageHeight: number;
  onField?: OnField;
}> = ({ items: placed, pageHeight, onField }) => {
  // A fixed item draws a multiline value's text, unformatted, as the PDF does.
  const items = placed.map((item) =>
    item.multiline && item.value
      ? { ...item, value: visibleText(item.value), multiline: false }
      : item
  );
  const added = items.map((item) => {
    if (!item.maxLines || !item.value) return 0;
    const lines = wrapText(item.value, item.width, item, measureText).length;
    return (Math.min(item.maxLines, lines) - 1) * (item.leading ?? item.size);
  });

  return (
    <>
      {items.map((item) => {
        const leading = item.leading ?? item.size;
        const pushedDown = items.reduce(
          (sum, other, otherIndex) =>
            other.baseline > item.baseline
              ? sum + (added[otherIndex] ?? 0)
              : sum,
          0
        );
        return (
          <div
            key={item.slot}
            className="absolute overflow-hidden"
            style={{
              left: item.x,
              top:
                pageHeight -
                item.baseline -
                baselineIn(leading, item.size) +
                pushedDown,
              width: item.width,
              ...textCss(item, leading),
              ...(item.maxLines
                ? {
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: item.maxLines
                  }
                : {})
            }}
          >
            <LineContext.Provider value={{ width: item.width, style: item }}>
              <FieldValue field={item} onField={onField} />
            </LineContext.Provider>
          </div>
        );
      })}
    </>
  );
};
